<template>
	<div
		class="stage-widget"
		:type="type"
		:compact="compact"
		:selected="selected"
		@click="handleClick"
	>
		<div class="stage-header">
			<!-- @slot Used to display stage widget header <br/> Note : use html element with 'title' class to display header -->
			<slot name="header" />
		</div>
		<div class="stage-body">
			<!-- @slot Used to display widget content -->
			<slot />
		</div>
		<div class="stage-footer">
			<!-- @slot Used to display widget footer-->
			<slot name="footer" />
		</div>
		<div class="tip" :position="tipPosition">
			<svg viewBox="0 0 100 200" class="vertical">
				<polygon points="0 0, 100 100, 0 200" />
			</svg>
			<svg viewBox="0 0 200 100" class="horizontal">
				<polygon points="0 0, 100 100, 200 0" />
			</svg>
		</div>
	</div>
</template>
<script>
import { defineComponent } from "vue";

export default defineComponent({
	name: "StageCard",
	props: {
		/**
		 * 'success' | 'error' | 'inProgress' | 'warning' | 'disabled' | 'standby' | 'paused'
		 */
		type: {
			type: String,
			default: "inProgress",
			validator(value) {
				return [
					"success",
					"error",
					"inProgress",
					"warning",
					"disabled",
					"standby",
					"paused"
				].includes(value);
			}
		},

		/**
		 * 'right' | 'left' | 'bottom' | 'top' | 'none'
		 */
		tipPosition: {
			type: String,
			default: "right",
			validator(value) {
				return ["right", "left", "bottom", "top", "none"].includes(value);
			}
		},
		/**
		 * 0 to 1
		 */
		scale: {
			type: Number,
			default: 1,
			validator(value) {
				return value >= 0 && value <= 1;
			}
		},
		/**
		 * true | false
		 */
		compact: {
			type: Boolean,
			default: false
		},
		/**
		 * true | false
		 */
		selected: {
			type: Boolean,
			default: false
		}
	},
	methods: {
		handleClick(e) {
			/**
			 * Click event.
			 *
			 * @event click
			 */
			this.$emit("click", e);
		}
	}
});
</script>
<style lang="scss">
$stage-card-colors: (
	"error": var(--dashboard-danger-gradient),
	"warning": var(--dashboard-warning-gradient),
	"disabled": var(--dashboard-font-light),
	"standby": var(--dashboard-font-light),
	"paused": var(--dashboard-font-light)
);
$stage-card-header-icon-color: var(--dashboard-icon-light);
$background-color: var(--dashboard-element-light);
$title-color: var(--dashboard-font-dark);
$disabled-color: var(--dashboard-font-light);

$hover: var(--dashboard-element-dark);
$icon-hover: var(--dashboard-primary);
$focus: var(--dashboard-primary);

$min-width: 360px;
$max-width: 560px;

@mixin left-right-tip {
	> .vertical {
		display: block;
	}
	> .horizontal {
		display: none;
	}
}

@mixin top-bottom-tip {
	.vertical {
		display: none;
	}
	.horizontal {
		display: block;
	}
}

.stage-widget {
	display: flex;
	flex-direction: column;
	min-width: $min-width;
	max-width: $max-width;
	background-color: $background-color !important;
	border-radius: 8px;
	position: relative;
	cursor: pointer;
	min-height: 134px;

	@each $type, $color in $stage-card-colors {
		@if $type == "disabled" or $type == "standby" or $type == "paused" {
			&[type="#{$type}"] {
				.stage-header > .title {
					color: $color !important;
				}
			}
		} @else {
			&[type="#{$type}"] {
				background: $color;
			}
		}
	}
	.stage-header {
		padding-top: 16px;
		padding-left: 16px;
		padding-right: 16px;
		padding-bottom: 16px;
		display: flex;
		align-items: center;
		justify-content: space-between;

		> .title {
			font-size: 12px;
			line-height: 12px;
			font-weight: 500;
			text-transform: uppercase;
			color: $title-color;
		}

		.icon-container {
			// height: 20px;
			// width: 20px;
			&:hover:not([disabled]) {
				& > [type="outlined"] > svg {
					stroke: $icon-hover;
				}

				& > [type="filled"] > svg {
					fill: $icon-hover;
				}
			}
			.icon[type="filled"] svg {
				fill: $stage-card-header-icon-color;
			}
		}
	}

	.stage-body {
		padding-left: 16px;
		padding-right: 16px;
	}

	.stage-footer {
		padding: 16px;
		margin-top: auto;
	}

	// tip container with svg
	.tip {
		> svg {
			fill: $background-color;
		}
		position: absolute;
		&[position="right"] {
			width: 12px;
			right: -12px;
			top: calc(50% - 12px);
			@include left-right-tip;
		}
		&[position="left"] {
			width: 12px;
			left: -12px;
			top: calc(50% - 12px);
			> svg {
				transform: rotate(180deg);
			}
			@include left-right-tip;
		}
		&[position="top"] {
			width: 24px;
			top: -12px;
			left: calc(50% - 12px);
			> svg {
				transform: rotate(180deg);
			}
			@include top-bottom-tip;
		}
		&[position="bottom"] {
			width: 24px;
			bottom: -12px;
			left: calc(50% - 12px);
			@include top-bottom-tip;
		}

		&[position="none"] {
			display: none;
		}
		&:before {
			content: "";
			border: 12px solid transparent;
			position: absolute;
			z-index: 1;
		}
	}

	&:active,
	&:hover:not(:active) {
		&:before {
			content: "";
			position: absolute;
			top: 0;
			right: 0;
			bottom: 0;
			left: 0;
			width: 100%;
			height: 100%;
			background-color: $hover;
			border-radius: 8px;
		}
		> .tip[position="right"]:before {
			border-left-color: $hover;
			right: -12px;
		}
		> .tip[position="left"]:before {
			border-right-color: $hover;
			left: -12px;
		}
		> .tip[position="top"]:before {
			border-bottom-color: $hover;
			top: -12px;
		}
		> .tip[position="bottom"]:before {
			border-top-color: $hover;
			bottom: -12px;
		}
	}

	&:active {
		&:before {
			opacity: 0.08;
			cursor: pointer;
		}

		> .tip:before {
			opacity: 0.08;
		}
	}

	&:hover:not(:active) {
		&:before {
			opacity: 0.04;
		}
		> .tip:before {
			opacity: 0.04;
		}
	}

	&:focus,
	&[selected] {
		box-shadow: inset 0 0 0 2px $focus;
		outline: none;

		> .tip {
			&:after {
				content: "";
				border: 14px solid transparent;
				position: absolute;
			}

			// outer border
			&[position="right"]:after {
				border-left-color: $focus;
				top: -2px;
			}
			&[position="left"]:after {
				border-right-color: $focus;
				top: -2px;
				right: 0;
			}
			&[position="top"]:after {
				border-bottom-color: $focus;
				left: -2px;
				bottom: 0;
			}
			&[position="bottom"]:after {
				border-top-color: $focus;
				left: -2px;
				top: 0;
			}
			// inner tip
			&[position="right"]:before {
				border-left-color: $background-color;
				right: -12px;
			}
			&[position="left"]:before {
				border-right-color: $background-color;
				left: -12px;
			}
			&[position="top"]:before {
				border-bottom-color: $background-color;
				top: -12px;
			}
			&[position="bottom"]:before {
				border-top-color: $background-color;
				bottom: -12px;
			}
		}
	}

	&[compact] {
		min-height: auto;
		> .stage-header {
			display: none;
		}
		> .stage-body {
			padding-top: 16px;

			> .stage-statistics-wrapper {
				> .stage-statistics {
					> .stage-status-text {
						display: none;
					}
					> .stage-number {
						display: none;
					}
				}
				> .stage-number {
					display: none;
				}
			}
		}

		> .stage-footer {
			margin-top: unset;
		}
	}
}

// .stage-widget-container {
// 	display: grid;
// 	grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
// 	grid-gap: 20px;
// 	padding-right: 20px;
// 	// padding: 24px;
// }

.stage-widget-container-flex-vertical {
	display: flex;
	flex-direction: column;
	grid-gap: 20px;
	// padding: 24px;
}
</style>
